import { MantineProvider } from "@mantine/core";
import { useState } from "react";

import { cssVariablesResolver, theme } from "~/utils/Theme/mantine";

const SCOPE_CLASS = "workos-dark-scope";

/**
 * Renders children in the app's normal dark colour scheme regardless of the
 * user's scheme — for WorkOS-mode surfaces that sit over the dark-pinned
 * login (org picker, T&C dialog). A nested MantineProvider emits its CSS
 * variables scoped to the wrapper (`cssVariablesSelector`) and stamps
 * `data-mantine-color-scheme="dark"` on it (`getRootElement`), so every
 * Mantine component inside resolves dark values while the rest of the page
 * keeps the user's scheme. Portalled overlays escape the wrapper — keep
 * dropdowns/popovers `withinPortal: false` inside this scope.
 */
export function DarkColorSchemeScope({
  children,
}: {
  children: React.ReactNode;
}) {
  const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null);

  return (
    <MantineProvider
      theme={theme}
      cssVariablesResolver={cssVariablesResolver}
      forceColorScheme="dark"
      cssVariablesSelector={`.${SCOPE_CLASS}`}
      getRootElement={() => rootElement ?? undefined}
      withGlobalClasses={false}
    >
      <div
        ref={setRootElement}
        className={SCOPE_CLASS}
        // Set in JSX, not left to the provider: its forceColorScheme effect
        // runs once on mount, before this element's ref exists, so it would
        // never stamp the attribute — and without it the scoped dark
        // variable block never matches.
        data-mantine-color-scheme="dark"
        style={{
          minHeight: "100dvh",
          backgroundColor: "var(--mantine-color-body)",
          // Text colour is normally set on <body> and inherited as the
          // computed (light-scheme) value; re-anchoring it here makes
          // inheritance resolve the variable inside the dark scope.
          color: "var(--mantine-color-text)",
        }}
      >
        {children}
      </div>
    </MantineProvider>
  );
}
